import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Drawer from '@shared/atoms/Drawer';
import FormInput from '@shared/atoms/FormFields/FormInput';
import FormToggle from '@shared/atoms/FormFields/FormToggle';
import FullWidthTabs from '@shared/atoms/FullWidthTabs';
import T from '@shared/atoms/Typography';
import Info from '@shared/molecules/Info';

import { LOCALES, DOMAIN_LOCALES, PROCESSING_STATUS } from '@shared/const';
import { useForm } from '@shared/utils';
import theme from '@shared/themes/default';

import { useAppGlobals } from '../../../contexts/AppContext';

import CoursesTab from './tabs/CoursesTab';
import DescriptionTab from './tabs/DescriptionTab';
import DomainsTab from './tabs/DomainsTab';
import LinksTab from './tabs/LinksTab';
import UploadsTab from './tabs/UploadsTab';

const DrawerContent = styled.div`
  background-color: ${theme.palette.darkWrapper};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.m1};
  margin: 0 ${theme.spacing.m1} ${theme.spacing.m1} ${theme.spacing.m1};
  padding: ${theme.spacing.s};
  width: 496px;
`;

const BlockWrapper = styled.div`
  background-color: ${theme.palette.darkDistinct};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.s};
  padding: ${theme.spacing.s};
`;

const ToggleRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${theme.spacing.s};
  justify-content: end;
`;

const getExpertLanguages = (expert) => {
  const languages = expert.domains.reduce((languages, domain) => {
    DOMAIN_LOCALES[domain].forEach((language, i) => {
      if (
        !languages.includes(language) &&
        // first language on the DOMAIN_LOCALES is the default/required
        (i === 0 ||
          // the language is present on the translatable fields
          !![...expert.description, ...expert.shortDescription].find(
            (description) => description.languageCode === language
          ))
      ) {
        languages.push(language);
      }
    });
    return languages;
  }, []);

  if (expert.domains.some((d) => d.domain === 'no')) {
    return languages.filter((lang) => lang !== 'es');
  }

  return languages;
};

const ExpertDrawer = ({
  expert,
  handleClose,
  handleCreate,
  handleCreateEnded,
  handleUpdate,
  handleUpdateEnded,
  open,
  showError,
  socialMediaTypes,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);

  const { domain } = useAppGlobals();
  const { t } = useTranslation();

  const availableLocales =
    domain === 'no' ? LOCALES.filter((loc) => loc !== 'es') : LOCALES;

  const {
    disableSubmit,
    edited,
    errors: formErrors,
    handleBlur,
    handleChange,
    handleSubmit,
    properties,
    reset,
    setDefaults,
    setRequiredLocales,
    submitting,
    values,
  } = useForm({
    changesRequired: true,
    fields: {
      description: {
        localised: true,
        maxLength: 1500,
        required: true,
      },
      domains: {},
      enabled: {},
      firstName: {
        required: true,
        maxLength: 50,
      },
      lastName: {
        required: true,
        maxLength: 50,
      },
      picture: {
        required: true,
      },
      shortDescription: {
        localised: true,
        maxLength: 250,
        required: true,
      },
      signature: {
        required: true,
      },
      socialLinks: {},
    },
    locales: availableLocales,
    onSubmitHook: (newExpert) => {
      const input = {
        domains: newExpert.domains,
        description: newExpert.description,
        disabled: !newExpert.enabled,
        firstName: newExpert.firstName,
        lastName: newExpert.lastName,
        shortDescription: newExpert.shortDescription,
      };
      const { picture, signature } = newExpert;

      if (picture?.processingStatus === PROCESSING_STATUS.TO_SCHEDULE) {
        const { key } = picture;
        input.picture = expert.id ? { updateFile: key } : key;
      } else if (!picture?.value && !picture?.processingStatus) {
        input.picture = null;
      }

      if (signature?.processingStatus === PROCESSING_STATUS.TO_SCHEDULE) {
        const { key } = signature;
        input.signature = expert.id ? { updateFile: key } : key;
      } else if (!signature?.value && !signature?.processingStatus) {
        input.signature = null;
      }

      if (expert.id) {
        input.id = expert.id;

        // Check languages
        const oldLanguages = expert.languages.map((language) => language.code);
        const newLanguages = getExpertLanguages(newExpert);

        const deleteLanguages = oldLanguages.filter(
          (oldLanguage) => !newLanguages.includes(oldLanguage)
        );
        const updateLanguages = newLanguages.filter(
          (newLanguage) => !oldLanguages.includes(newLanguage)
        );

        if (deleteLanguages.length || updateLanguages.length) {
          input.languages = {
            ...(deleteLanguages.length ? { deleteLanguages } : {}),
            ...(updateLanguages.length ? { updateLanguages } : {}),
          };
        }

        const createSocialLinks = newExpert.socialLinks.filter(
          (newLink) => !newLink.id
        );

        const updateSocialLinks = newExpert.socialLinks.filter((newLink) => {
          const existingLink = expert.socialLinks.find(
            (oldLink) => newLink.id === oldLink.id
          );
          return existingLink && newLink.url !== existingLink.url;
        });

        const newLinkIds = newExpert.socialLinks.map((link) => link.id);
        const deleteSocialLinks = expert.socialLinks
          .filter((link) => {
            return !newLinkIds.includes(link.id);
          })
          .map((link) => link.id);

        if (
          createSocialLinks.length ||
          updateSocialLinks.length ||
          deleteSocialLinks.length
        ) {
          input.socialLinks = {
            ...(createSocialLinks.length ? { create: createSocialLinks } : {}),
            ...(updateSocialLinks.length ? { update: updateSocialLinks } : {}),
            ...(deleteSocialLinks.length ? { delete: deleteSocialLinks } : {}),
          };
        }

        handleUpdate({ variables: { input } });
      } else {
        input.languages = getExpertLanguages(newExpert);
        if (newExpert.socialLinks.length) {
          input.socialLinks = newExpert.socialLinks.map((link) => {
            const { socialMediaTypeId, url } = link;
            return {
              socialMediaTypeId,
              url,
            };
          });
        }

        handleCreate({ variables: { input } });
      }
    },
    onSubmitHookEnded: expert?.id ? handleUpdateEnded : handleCreateEnded,
  });

  useEffect(() => {
    if (expert) {
      const expertSocialLinks =
        expert.socialLinks?.map(({ id, socialMediaType, url }) => ({
          id,
          socialMediaTypeId: socialMediaType.id,
          url,
        })) || [];
      setDefaults({
        description: expert.description || [],
        domains: expert.domains?.map(({ domain }) => domain) || [domain],
        enabled: !expert?.disabled,
        firstName: expert.firstName ?? '',
        lastName: expert.lastName ?? '',
        picture: {
          key: expert.picture?.imageFiles?.[0]?.key,
          processingStatus: expert.picture?.file?.processingStatus,
        },
        shortDescription: expert.shortDescription || [],
        signature: {
          key: expert.signature?.imageFiles?.[0]?.key,
          processingStatus: expert.signature?.file?.processingStatus,
        },
        socialLinks: expertSocialLinks || [],
      });
      setSelectedTab(0);
    } else {
      reset();
    }
  }, [expert]);

  useEffect(() => {
    if (values.domains) {
      const requiredLocales = values.domains.map(
        (domain) => DOMAIN_LOCALES[domain][0]
      );
      const filteredRequiredLocales = values.domains.includes('no')
        ? requiredLocales.filter((loc) => loc !== 'es')
        : requiredLocales;
      setRequiredLocales(filteredRequiredLocales);
    }
  }, [values.domains]);

  return (
    <Drawer
      anchor="right"
      edited={edited}
      errorMessage={showError ? t('backoffice.experts.errorSaving') : undefined}
      open={open}
      onClose={handleClose}
      onSave={handleSubmit}
      title={
        expert?.id
          ? `${expert.firstName} ${expert.lastName}`
          : t('backoffice.experts.new')
      }
      saveDisabled={disableSubmit || !edited || submitting}
      saving={submitting}
    >
      <DrawerContent>
        <ToggleRow>
          <Info theme="dark">{t('backoffice.experts.listedExplanation')}</Info>
          <T fontWeight="medium">{t('backoffice.experts.unlisted')}</T>
          <FormToggle
            id="enabled"
            label={t('backoffice.experts.listed')}
            name="enabled"
            handleChange={handleChange}
            properties={properties}
            values={values}
          />
        </ToggleRow>
        <BlockWrapper>
          <FormInput
            id="firstName"
            label={t('backoffice.experts.firstname')}
            name="firstName"
            placeholder={t('backoffice.experts.firstnamePlaceholder')}
            theme="dark"
            errors={formErrors}
            handleBlur={handleBlur}
            handleChange={handleChange}
            properties={properties}
            values={values}
          />
          <FormInput
            id="lastName"
            label={t('backoffice.experts.lastname')}
            name="lastName"
            placeholder={t('backoffice.experts.lastnamePlaceholder')}
            errors={formErrors}
            handleBlur={handleBlur}
            handleChange={handleChange}
            properties={properties}
            values={values}
          />
        </BlockWrapper>
        <BlockWrapper>
          <FullWidthTabs
            onChangeTabHandler={(index) => setSelectedTab(index)}
            selectedTabIndex={selectedTab}
            tabsToSelect={[
              {
                label: t('backoffice.experts.description'),
                component: (
                  <DescriptionTab
                    formErrors={formErrors}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    properties={properties}
                    submitting={submitting}
                    values={values}
                  />
                ),
              },
              {
                label: t('backoffice.experts.links'),
                component: (
                  <LinksTab
                    handleChange={handleChange}
                    socialMediaTypes={socialMediaTypes}
                    values={values}
                  />
                ),
              },
              {
                label: t('backoffice.experts.uploads'),
                component: (
                  <UploadsTab
                    handleChange={handleChange}
                    properties={properties}
                    values={values}
                  />
                ),
              },
              {
                label: t('backoffice.experts.courses'),
                component: <CoursesTab courses={expert?.courses} />,
              },
              {
                label: t('backoffice.experts.domains'),
                component: (
                  <DomainsTab
                    domains={values?.domains || []}
                    domain={domain}
                    onChange={handleChange}
                  />
                ),
              },
            ]}
            theme="dark"
          />
        </BlockWrapper>
      </DrawerContent>
    </Drawer>
  );
};

ExpertDrawer.propTypes = {
  expert: PropTypes.object,
  handleClose: PropTypes.func.isRequired,
  handleCreate: PropTypes.func.isRequired,
  handleCreateEnded: PropTypes.bool,
  handleUpdate: PropTypes.func.isRequired,
  handleUpdateEnded: PropTypes.bool,
  open: PropTypes.bool,
  showError: PropTypes.bool,
  socialMediaTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
};

export default ExpertDrawer;
